/**
 * BloggerCard 组件
 * 共享的博主卡片组件，用于展示博主信息
 * Electron 端和 Cloud 端共用
 */

import React, { useState } from 'react';
import { Button, Tag as AntTag, Tooltip } from 'antd';
import { DeleteOutlined, SyncOutlined, LinkOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { NoteBlankIcon } from '@phosphor-icons/react';
import { formatCount } from '../utils/postList';

/**
 * 博主数据基础接口
 * 兼容 Blogger (Electron) 和 CloudBlogger (Cloud) 类型
 * 注意：使用 undefined | null 来兼容两端的类型定义
 */
export interface BloggerData {
  id: string;
  profileUrl: string;
  nickname?: string | null;
  avatarUrl?: string | null;
  description?: string | null;
  annotation?: string | null;
  followersCount?: number | null;
  totalLikedCount?: number | null;
  isTracked?: boolean;
  createdAt?: string;
  updatedAt?: string;
  tags?: Array<{ id: string; name: string }>;
  posts?: Array<any>;
  _count?: {
    posts?: number;
  };
}

export interface BloggerCardProps<T extends BloggerData = BloggerData> {
  /** 博主数据 */
  blogger: T;
  /** 编辑标签回调 */
  onEditTags?: (blogger: T) => void;
  /** 编辑注解回调 */
  onEditAnnotation?: (blogger: T) => void;
  /** 更新博主数据回调（通过扩展自动获取，Electron 专用） */
  onUpdateData?: (blogger: T) => void;
  /** 删除/取消追踪博主回调 */
  onDelete?: (blogger: T) => void;
  /** 查看博主帖子回调 */
  onViewPosts?: (blogger: T) => void;
  /** 打开博主主页链接（如果不传则使用默认行为） */
  onOpenProfile?: (url: string) => void;
  /** 是否显示查看帖子按钮 */
  showViewPostsButton?: boolean;
  /** 是否显示更新数据按钮（Electron 专用） */
  showUpdateDataButton?: boolean;
  /** 平台类型 */
  platform?: 'web' | 'electron';
}

export function BloggerCard<T extends BloggerData = BloggerData>({
  blogger,
  onEditTags,
  onEditAnnotation,
  onUpdateData,
  onDelete,
  onViewPosts,
  onOpenProfile,
  showViewPostsButton = false,
  showUpdateDataButton = false,
  platform = 'web',
}: BloggerCardProps<T>) {
  const [isHovered, setIsHovered] = useState(false);
  const [isAddNoteHovered, setIsAddNoteHovered] = useState(false);

  const handleOpenProfile = () => {
    if (!blogger.profileUrl) return;

    if (onOpenProfile) {
      onOpenProfile(blogger.profileUrl);
    } else if (platform === 'web') {
      window.open(blogger.profileUrl, '_blank');
    }
  };

  // 计算帖子数量
  const postCount = blogger._count?.posts ?? blogger.posts?.length ?? 0;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        background: 'var(--ant-color-bg-container)',
        padding: '20px 16px 20px 20px',
        marginBottom: 3,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        transition: 'box-shadow 0.2s',
        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      {/* 左侧：头像 + 信息 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
        {/* 头像 */}
        {blogger.avatarUrl ? (
          <img
            src={blogger.avatarUrl}
            alt={blogger.nickname || '博主'}
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              flexShrink: 0,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'var(--ant-color-fill-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'var(--ant-color-text-tertiary)',
              fontSize: 20,
            }}
          >
            {(blogger.nickname || '?')[0]}
          </div>
        )}

        {/* 信息区域 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 第一行：昵称 + 粉丝数 + 帖子数 */}
          <h5
            style={{
              margin: 0,
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Tooltip title={blogger.profileUrl ? '点击访问主页' : undefined}>
              <span
                onClick={blogger.profileUrl ? handleOpenProfile : undefined}
                style={{
                  cursor: blogger.profileUrl ? 'pointer' : 'default',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (blogger.profileUrl) {
                    e.currentTarget.style.color = 'var(--ant-color-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'inherit';
                }}
              >
                {blogger.nickname || '未知博主'}
              </span>
            </Tooltip>

            {blogger.profileUrl && (
              <LinkOutlined
                onClick={handleOpenProfile}
                style={{
                  fontSize: 12,
                  color: 'var(--ant-color-text-quaternary)',
                  cursor: 'pointer',
                }}
              />
            )}

            {blogger.followersCount != null && (
              <span
                style={{
                  fontWeight: 'normal',
                  fontSize: 13,
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                粉丝 {formatCount(blogger.followersCount)}
              </span>
            )}

            {postCount > 0 && (
              <span
                style={{
                  fontWeight: 'normal',
                  fontSize: 13,
                  color: 'var(--ant-color-text-quaternary)',
                }}
              >
                · {postCount} 篇帖子
              </span>
            )}
          </h5>

          {/* 第二行：描述 */}
          {blogger.description && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--ant-color-text-secondary)',
                marginTop: 4,
                marginBottom: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {blogger.description}
            </div>
          )}

          {/* 第三行：标签 */}
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            {blogger.tags && blogger.tags.length > 0 ? (
              blogger.tags.map((tag) => (
                <AntTag key={tag.id} style={{ marginRight: 0 }}>
                  {tag.name}
                </AntTag>
              ))
            ) : (
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--ant-color-text-tertiary)',
                  border: '1px dashed var(--ant-color-border)',
                  borderRadius: 4,
                  padding: '0 4px',
                  lineHeight: '18px',
                  display: 'inline-block',
                  cursor: 'default',
                  userSelect: 'none',
                }}
              >
                暂无标签
              </span>
            )}
            {onEditTags && (
              <Tooltip title="编辑标签">
                <EditOutlined
                  onClick={() => onEditTags(blogger)}
                  style={{
                    fontSize: 14,
                    marginLeft: 4,
                    color: 'var(--ant-color-text-tertiary)',
                    cursor: 'pointer',
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.2s',
                  }}
                />
              </Tooltip>
            )}
          </div>

        </div>
      </div>

      {/* 左上角备注气泡 */}
      {blogger.annotation ? (
        <Tooltip title={onEditAnnotation ? "点击编辑备注" : undefined}>
          <div
            onClick={onEditAnnotation ? () => onEditAnnotation(blogger) : undefined}
            style={{
              position: 'absolute',
              top: -4,
              left: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              fontWeight: 600,
              color: '#333333',
              padding: '3px 7px',
              backgroundColor: '#FFD95C',
              border: '1px solid #FFC64D',
              borderRadius: '14px 14px 14px 2px',
              maxWidth: '100%',
              cursor: onEditAnnotation ? 'pointer' : 'default',
              transition: 'filter 0.2s',
            }}
            onMouseEnter={(e) => {
              if (onEditAnnotation) e.currentTarget.style.filter = 'brightness(0.95)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'none';
            }}
          >
            <NoteBlankIcon size={14} weight="bold" style={{ flexShrink: 0, transform: 'rotate(-4deg)' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {blogger.annotation}
            </span>
          </div>
        </Tooltip>
      ) : onEditAnnotation ? (

        <div
          onClick={() => onEditAnnotation(blogger)}
          onMouseEnter={() => setIsAddNoteHovered(true)}
          onMouseLeave={() => setIsAddNoteHovered(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            color: 'var(--ant-color-text-secondary)',
            padding: '4px 8px',
            // backgroundColor: 'var(--ant-color-fill-tertiary)',
            borderRadius: '14px 14px 14px 2px',
            cursor: 'pointer',
            opacity: isHovered ? 0.8 : 0.5,
            transition: 'all 0.2s ease',
            overflow: 'hidden',
          }}
        >
          <NoteBlankIcon size={14} style={{ flexShrink: 0, transform: 'rotate(-4deg)' }} />
          <span
            style={{
              maxWidth: isAddNoteHovered ? 100 : 0,
              opacity: isAddNoteHovered ? 1 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
          >
            添加备注
          </span>
        </div>

      ) : null}

      {/* 右侧：操作按钮 */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexShrink: 0,
          opacity: isHovered ? 1 : 0.6,
          transition: 'opacity 0.2s',
        }}
      >
        {showViewPostsButton && onViewPosts && (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewPosts(blogger)}
          >
            查看帖子
          </Button>
        )}

        {showUpdateDataButton && onUpdateData && (
          <Button
            size="small"
            icon={<SyncOutlined />}
            onClick={() => onUpdateData(blogger)}
          >
            更新数据
          </Button>
        )}

        {onDelete && (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(blogger)}
          >
            取消追踪
          </Button>
        )}
      </div>
    </div>
  );
};

export default BloggerCard;
